export const GLOSSARY = [
  {
    term: "SIP",
    full: "Systematic Investment Plan",
    short: "Investing a fixed amount at regular intervals — usually every month — instead of waiting to build a lump sum.",
    meaning: "A SIP means investing a fixed amount at regular intervals, usually every month. Instead of waiting to build a large lump sum, you start small and invest with discipline over time.",
    example: "If you invest Rs. 5,000 every month into a mutual fund, that is a SIP. It helps build a habit of investing without needing to time the market.",
    why: "SIPs make investing more manageable, especially for salaried individuals, new investors, and long-term goal planning.",
  },
  {
    term: "STP",
    full: "Systematic Transfer Plan",
    short: "Moving money gradually from one mutual fund scheme to another in a structured way.",
    meaning: "An STP allows money to move gradually from one mutual fund scheme to another, usually from a lower-risk fund to a higher-risk fund or vice versa, in a structured way.",
    example: "If you invest Rs. 6 lakh in a liquid fund today and transfer Rs. 50,000 every month into an equity fund, that is an STP.",
    why: "It can help when you do not want to put a large amount into the market all at once.",
  },
  {
    term: "SWP",
    full: "Systematic Withdrawal Plan",
    short: "Withdrawing a fixed amount from your mutual fund investment at regular intervals.",
    meaning: "An SWP allows you to withdraw a fixed amount from your mutual fund investment at regular intervals, such as monthly or quarterly.",
    example: "If you have built a retirement corpus and want Rs. 25,000 every month from it, you may use an SWP.",
    why: "It is often useful for retirees or anyone looking for a structured cash flow from investments.",
  },
  {
    term: "NAV",
    full: "Net Asset Value",
    short: "The per-unit value of a mutual fund scheme on a given day.",
    meaning: "NAV is the per-unit value of a mutual fund scheme on a given day. In simple terms, it is the price at which you buy or redeem units, based on applicable rules.",
    example: "If a fund's NAV is Rs. 25 and you invest Rs. 5,000, you will get units worth that amount at the applicable NAV.",
    why: "Many investors think a lower NAV means a cheaper or better fund. That is not necessarily true. A fund should be understood by its strategy, suitability, and performance consistency, not just NAV.",
  },
  {
    term: "AMC",
    full: "Asset Management Company",
    short: "The company that launches and manages mutual fund schemes.",
    meaning: "An AMC is the company that launches and manages mutual fund schemes.",
    example: "If you invest in a mutual fund offered by an asset management company, the AMC is the institution managing the pooled money through professional fund managers.",
    why: "It helps investors understand who is behind the scheme and who manages the fund operations.",
  },
  {
    term: "AUM",
    full: "Assets Under Management",
    short: "The total value of money being managed in a mutual fund or by an AMC.",
    meaning: "AUM means the total value of money being managed in a mutual fund or by an AMC.",
    example: "If a fund has thousands of investors and together they have invested Rs. 2,000 crore, that amount forms part of its AUM.",
    why: "AUM gives scale, but bigger is not always better. It is only one factor and should not be the sole basis for choosing a fund.",
  },
  {
    term: "KYC",
    full: "Know Your Customer",
    short: "The identity verification process required before you can invest in mutual funds.",
    meaning: "KYC is the identity verification process required before you can invest in mutual funds.",
    example: "Submitting your PAN, address proof, photograph, and completing verification is part of KYC.",
    why: "Without completing KYC, you generally cannot start investing.",
  },
  {
    term: "PAN",
    full: "Permanent Account Number",
    short: "Your tax identity number, commonly required for mutual fund investments in India.",
    meaning: "PAN is your tax identity number and is commonly required for mutual fund investments in India.",
    example: "When opening your investment account or completing KYC, PAN is one of the key documents required.",
    why: "It is a basic compliance requirement for most financial transactions.",
  },
  {
    term: "NFO",
    full: "New Fund Offer",
    short: "The first subscription period of a newly launched mutual fund scheme.",
    meaning: "An NFO is the first subscription period of a newly launched mutual fund scheme.",
    example: "When a fund house launches a brand-new scheme and invites investors to subscribe during the launch window, that is an NFO.",
    why: "A new fund being available at an initial price does not automatically make it better. Suitability matters more than novelty.",
  },
  {
    term: "ELSS",
    full: "Equity Linked Savings Scheme",
    short: "A tax-saving mutual fund category that comes with a lock-in period.",
    meaning: "ELSS is a tax-saving mutual fund category that comes with a lock-in period.",
    example: "An investor looking to save tax under the applicable tax regime may consider ELSS if it suits their risk profile and planning needs.",
    why: "It combines market-linked investing with tax-related utility, but it should still be selected carefully based on goals and risk appetite.",
  },
  {
    term: "IDCW",
    full: "Income Distribution cum Capital Withdrawal",
    short: "The term now used in place of the older word 'dividend' in mutual funds.",
    meaning: "IDCW is the term now used in place of the older word \"dividend\" in mutual funds.",
    example: "If a scheme announces a payout under the IDCW option, that amount is paid out from the scheme as per applicable rules.",
    why: "Many investors assume it is extra return. In reality, the payout comes from the scheme's own money and affects the fund value accordingly.",
  },
  {
    term: "TER",
    full: "Total Expense Ratio",
    short: "The annual cost charged by the fund for managing your investment.",
    meaning: "TER is the annual cost charged by the fund for managing your investment.",
    example: "This includes fund management and operating expenses, and it is factored into the scheme.",
    why: "Costs matter over the long term, so investors should understand expenses along with returns, risk, and suitability.",
  },
  {
    term: "Exit Load",
    full: "Exit Load",
    short: "A charge that may apply if you redeem your investment before a specified period.",
    meaning: "Exit load is a charge that may apply if you redeem your investment before a specified period.",
    example: "If a scheme has a 1 percent exit load for redemptions within one year, an early withdrawal may attract that charge.",
    why: "It affects liquidity and should be understood before investing.",
  },
  {
    term: "Lock-in Period",
    full: "Lock-in Period",
    short: "The minimum time during which the investment cannot be redeemed.",
    meaning: "A lock-in period is the minimum time during which the investment cannot be redeemed.",
    example: "ELSS funds come with a lock-in period, which means you cannot withdraw the money before that period ends.",
    why: "It is important for planning, especially if you may need access to funds earlier.",
  },
  {
    term: "Benchmark",
    full: "Benchmark",
    short: "A reference index used to compare how a mutual fund is performing.",
    meaning: "A benchmark is a reference index used to compare how a mutual fund is performing.",
    example: "An equity fund may be compared against a market index to assess how it has performed relative to that reference.",
    why: "It helps investors evaluate fund performance in context instead of viewing returns in isolation.",
  },
  {
    term: "Folio Number",
    full: "Folio Number",
    short: "Your unique account number with a mutual fund house.",
    meaning: "A folio number is your unique account number with a mutual fund house.",
    example: "When you invest with a fund house, your holdings there are usually tracked under a folio number.",
    why: "It helps identify and manage your investments efficiently.",
  },
];
